import { useState } from 'react'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { schemaChangementMotDePasse } from '@afs/shared'
import { Bandeau } from '../../../shared/ui/Bandeau.js'
import { Bouton } from '../../../shared/ui/Bouton.js'
import { Champ } from '../../../shared/ui/Champ.js'
import { ChampCode } from '../../../shared/ui/ChampCode.js'
import { estErreurApi } from '../../../shared/api/erreurs.js'
import {
  changerMotDePasseProvisoire,
  clesAuth,
  confirmerEnrolementInitial,
  preparerEnrolementInitial,
} from '../api.js'
import { useOuvrirSession } from '../session.js'
import styles from './Panneau.module.css'

/** Ce que la connexion a transmis : l'étape à franchir, et son jeton. */
interface EtatNavigation {
  readonly etape?: 'mot_de_passe' | 'enrolement'
  readonly jetonTemporaire?: string
}

/**
 * Première connexion — les deux étapes qui séparent un compte créé par un
 * administrateur d'une session utilisable.
 *
 * L'ordre n'est pas arbitraire : **mot de passe d'abord, second facteur
 * ensuite**. On ne lie pas un second facteur à un compte dont le titulaire ne
 * contrôle pas encore le premier — tant que le mot de passe est celui qu'un
 * administrateur a choisi, ce n'est pas encore vraiment son compte.
 *
 * Le jeton transite par l'état de navigation, jamais par l'URL : une URL part
 * dans l'historique, dans les logs du proxy et dans les captures d'écran.
 */
export function PremiereConnexion(): React.JSX.Element {
  const emplacement = useLocation()
  const etat = emplacement.state as EtatNavigation | null

  const [etape, setEtape] = useState(etat?.etape ?? 'mot_de_passe')
  const [jeton, setJeton] = useState(etat?.jetonTemporaire ?? '')

  // Arrivée directe ou rechargement : il n'y a rien à franchir sans jeton.
  if (!etat?.jetonTemporaire) return <Navigate to="/connexion" replace />

  return etape === 'mot_de_passe' ? (
    <EtapeMotDePasse
      jeton={jeton}
      onEnrolement={(nouveauJeton) => {
        setJeton(nouveauJeton)
        setEtape('enrolement')
      }}
    />
  ) : (
    <EtapeEnrolement jeton={jeton} />
  )
}

/** Étape 1 — remplacer le mot de passe provisoire. */
function EtapeMotDePasse({
  jeton,
  onEnrolement,
}: {
  jeton: string
  onEnrolement: (jeton: string) => void
}): React.JSX.Element {
  const naviguer = useNavigate()
  const ouvrirSession = useOuvrirSession()
  const [motDePasse, setMotDePasse] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [erreurLocale, setErreurLocale] = useState<string | undefined>(undefined)

  const envoi = useMutation({
    mutationFn: (valeur: string) => changerMotDePasseProvisoire(valeur, jeton),
    onSuccess: (suite) => {
      // Le serveur dit ce qui vient après : le front n'a rien à deviner.
      if (suite.statut === 'enrolement_requis') {
        onEnrolement(suite.jetonTemporaire)
        return
      }
      if (suite.statut === 'poste_a_declarer') {
        void naviguer('/connexion/poste', {
          replace: true,
          state: { jetonTemporaire: suite.jetonTemporaire },
        })
        return
      }
      ouvrirSession(suite)
      void naviguer('/', { replace: true })
    },
  })

  function soumettre(evenement: React.SyntheticEvent): void {
    evenement.preventDefault()

    // La confirmation ne se valide pas côté serveur : elle n'existe que pour
    // rattraper une faute de frappe avant de la rendre définitive.
    if (motDePasse !== confirmation) {
      setErreurLocale('Les deux saisies diffèrent.')
      return
    }

    const valide = schemaChangementMotDePasse.safeParse({ motDePasse })
    if (!valide.success) {
      setErreurLocale(z.flattenError(valide.error).fieldErrors.motDePasse?.[0])
      return
    }

    setErreurLocale(undefined)
    envoi.mutate(motDePasse)
  }

  return (
    <div className={styles.ecran}>
      <form className={styles.carte} onSubmit={soumettre} noValidate>
        <div className={styles.entete}>
          <h1 className={styles.titre}>Choisissez votre mot de passe</h1>
          <p className={styles.sousTitre}>
            Celui qui vous a été transmis est provisoire : la personne qui a créé votre compte le
            connaît. Il cesse d’être valable dès maintenant.
          </p>
        </div>

        {envoi.error && (
          <Bandeau
            ton="erreur"
            reference={estErreurApi(envoi.error) ? envoi.error.regle : undefined}
          >
            {envoi.error instanceof Error ? envoi.error.message : 'Changement impossible.'}
          </Bandeau>
        )}

        <Champ
          libelle="Nouveau mot de passe"
          type="password"
          autoComplete="new-password"
          required
          autoFocus
          value={motDePasse}
          aide="Douze caractères au minimum, dont une majuscule, une minuscule et un chiffre."
          erreur={erreurLocale}
          onChange={(evenement) => {
            setMotDePasse(evenement.target.value)
          }}
        />

        <Champ
          libelle="Confirmez le mot de passe"
          type="password"
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={(evenement) => {
            setConfirmation(evenement.target.value)
          }}
        />

        <Bouton type="submit" pleineLargeur enCours={envoi.isPending}>
          Enregistrer et continuer
        </Bouton>
      </form>
    </div>
  )
}

/**
 * Étape 2 — enrôlement imposé par le rôle (CCT § 5.1).
 *
 * Identique à l'enrôlement volontaire, à une différence près : la
 * confirmation OUVRE la session. Présenter un code valide EST la
 * vérification du second facteur — en redemander un juste après serait une
 * friction sans aucun gain.
 */
function EtapeEnrolement({ jeton }: { jeton: string }): React.JSX.Element {
  const naviguer = useNavigate()
  const ouvrirSession = useOuvrirSession()
  const [code, setCode] = useState('')

  /**
   * L'activation écrit un secret côté serveur, donc c'est une mutation au
   * sens métier. Elle est pourtant déclarée en `useQuery` : elle se déclenche
   * à l'affichage de l'écran, pas sur une action, et c'est le cycle de vie
   * d'une query.
   *
   * Ce n'est pas cosmétique. La version précédente appelait `mutate()` dans
   * le corps du composant, pendant le rendu. L'appel partait bien et le
   * serveur répondait, mais React n'était pas encore abonné au résultat : la
   * réponse arrivait dans le vide et l'écran restait sur « Préparation de
   * l'enrôlement… », indéfiniment. Direction et Super administrateur ne
   * pouvaient plus ouvrir de session du tout.
   *
   * Même réglage que l'écran d'enrôlement volontaire : un secret par visite,
   * jamais rejoué sur re-montage, sinon la clé changerait sous les yeux de
   * l'utilisateur au milieu de la saisie.
   */
  const activation = useQuery({
    queryKey: [...clesAuth.tout, 'enrolement-initial'],
    queryFn: () => preparerEnrolementInitial(jeton),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  })

  const confirmation = useMutation({
    mutationFn: (valeur: string) => confirmerEnrolementInitial(valeur, jeton),
    onSuccess: (resultat) => {
      if (resultat.statut !== 'session') {
        void naviguer('/connexion/poste', {
          replace: true,
          state: { jetonTemporaire: resultat.jetonTemporaire },
        })
        return
      }
      ouvrirSession(resultat)
      void naviguer('/', { replace: true })
    },
    onError: () => {
      setCode('')
    },
  })

  return (
    <div className={styles.ecran}>
      <div className={`${styles.carte} ${styles.carteLarge}`}>
        <div className={styles.entete}>
          <h1 className={styles.titre}>Activez votre second facteur</h1>
          <p className={styles.sousTitre}>
            Votre rôle l’exige. Cette étape ne se fait qu’une fois, et vous êtes seul à connaître le
            code.
          </p>
        </div>

        {activation.error && (
          <Bandeau
            ton="erreur"
            reference={estErreurApi(activation.error) ? activation.error.requestId : undefined}
          >
            {activation.error instanceof Error
              ? activation.error.message
              : 'Préparation impossible.'}
          </Bandeau>
        )}

        {activation.isPending && <p className={styles.mention}>Préparation de l’enrôlement…</p>}

        {activation.data && (
          <>
            <div className={styles.enrolement}>
              <div className={styles.qr}>
                <QRCodeSVG
                  value={activation.data.uri}
                  size={168}
                  level="M"
                  marginSize={0}
                  title="Code d’enrôlement du second facteur"
                />
              </div>

              <div className={styles.replis}>
                <p className={styles.sousTitre}>
                  Scannez ce code avec Google Authenticator, Microsoft Authenticator ou FreeOTP.
                  Impossible de scanner&nbsp;? Saisissez cette clé à la main :
                </p>
                <div className={styles.cle}>{formaterCle(activation.data.secretBase32)}</div>
              </div>
            </div>

            <div className={styles.separateur} />

            <form
              className={styles.etape}
              onSubmit={(evenement) => {
                evenement.preventDefault()
                confirmation.mutate(code)
              }}
            >
              {confirmation.error && (
                <Bandeau
                  ton="erreur"
                  reference={
                    estErreurApi(confirmation.error) ? confirmation.error.requestId : undefined
                  }
                >
                  {confirmation.error instanceof Error
                    ? confirmation.error.message
                    : 'Confirmation impossible.'}
                </Bandeau>
              )}

              <ChampCode
                libelle="Code affiché par l’application"
                valeur={code}
                onChange={setCode}
                disabled={confirmation.isPending}
                aide="La confirmation ouvre directement votre session."
              />

              <Bouton
                type="submit"
                pleineLargeur
                enCours={confirmation.isPending}
                disabled={code.length < 6}
              >
                Activer et se connecter
              </Bouton>

              <p className={styles.mention}>
                Si vous quittez avant de confirmer, l’enrôlement est à reprendre depuis le début. En
                cas de perte du téléphone, seul un Super administrateur réinitialise.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

/** Groupes de quatre : une clé base32 se recopie à la main, pas au scanner. */
function formaterCle(cle: string): string {
  return cle.replace(/(.{4})/g, '$1 ').trim()
}
